import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { CallToAction } from '../../components/common/blocks';
import AccordionList, {
  IAccordionListItem,
} from '../../components/common/blocks/AccordionList';
import { ICallToActionProps } from '../../components/common/blocks/CallToAction';
import IconsWithBulletLists, {
  IIconsWithBulletListsProps,
} from '../../components/common/blocks/IconsWithBulletLists';
import ReviewsCarousel, {
  IReviewsCarouselProps,
} from '../../components/common/blocks/ReviewsCarousel';
import { ITrustedByProps } from '../../components/common/blocks/TrustedBy';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import { HeroCountry, SellingPoints } from '../../components/country';
import BoxedCallToAction, {
  IBoxedCallToActionProps,
} from '../../components/country/BoxedCallToAction';
import { IHeroCountry } from '../../components/country/HeroCountry';
import KeyFacts, { IKeyFactsProps } from '../../components/country/KeyFacts';
import RelatedServices, {
  IRelatedService,
} from '../../components/country/RelatedServices';
import RequestCallToAction, {
  IRequestCallToActionProps,
} from '../../components/country/RequestCallToAction';
import { ISellingPoints } from '../../components/country/SellingPoints';
import { useAppSelector } from '../../hooks';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IApplicationClassPrice,
  IBusinessKeyValuesComponent,
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICountryPage,
  ICountryRelatedService,
  ICurrency,
  ICustomerReview,
  ICustomerReviewsComponent,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  IMedia,
  IMetaComponent,
  IServiceListComponent,
  ITrademarkRegistrationCountryLanding,
  ITrustedByComponent,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getAppCurrencies,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToContactUs,
  scrollToElement,
} from '../../shared/useCommon';
import {
  generateCustomerReviewsSchema,
  generateFaqsSchema,
  generateOrganizationSchema,
  generateProductSchema,
  generateTrademarkCountryLandingServiceSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import { replaceContentPlaceholders } from '../../shared/useTransformations';
import {
  calculateServicePricesWithCurrency,
  flatStrapiCollection,
  flatStrapiObject,
  formatDate,
  generateRandomId,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getTranslation,
  keepObjectProperties,
  removeObjectProperties,
} from '../../shared/useUtils';
import { IAppPage } from '../../store/slices/appSlice';
import { NextPageWithLayout } from '../_app';

interface ITrademarkRegistrationCountryLandingProps extends ILayoutPageProps {
  heroTipsTitle: string;
  heroTipOne: string;
  heroTipTwo: string;
  heroTipThree: string;
  businessKeyValues: IBusinessKeyValuesComponent;
  trustedBy: ITrustedByComponent;
  customerReviews: ICustomerReviewsComponent;
  reviews: ICustomerReview[];
  pageTitle: string;
  pageTitleHighlightedText?: string | null;
  pageSubTitle: string;
  fromLabel: string;
  feesIncludedLabel: string;
  orderNowLabel: string;
  howToCallToAction: ICallToActionComponent;
  doItYourselfVsProfessionalTitle: string;
  doItYourselfList: IServiceListComponent;
  professionalList: IServiceListComponent;
  faqsTitle?: string;
  faqs?: IAccordionListItem[];
  formSuccessMessage: string;
  formErrorMessage: string;
  contactMethods: IContactMethod[];
  contactUsForm: IContactUsForm;
  countryIso: string;
  countryIso3: string;
  countryApplicationClassPrices: IApplicationClassPrice[];
  countrySearchClassPrices: IApplicationClassPrice[];
  finalFees: IApplicationClassPrice[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  trademarkLink: string;
  relatedServicesTitle: string;
  relatedServices: IRelatedService[];
  featuredServicesCallToActionProps: ICallToActionProps;
  topCallToAction: ICallToActionComponent;
  bottomCallToAction: ICallToActionComponent;
  customerReviewsMeta: ICustomerReviewsMeta;
  customerReviewsTopTipStart: string;
  customerReviewsTopTipEnd: string;
  customerReviewsBottomTip: string;
  estimatedProcessingMonths?: string;
  yearsOfValidity?: string;
  requiredDocuments?: string;
  publicPriceNotes?: string;
  countryKeyFactsProps: IKeyFactsProps;
}

const TrademarkRegistrationCountryLandingPage: NextPageWithLayout<
  ITrademarkRegistrationCountryLandingProps
> = (props: ITrademarkRegistrationCountryLandingProps) => {
  const router = useRouter();
  const appState = useAppSelector((state) => state.app);
  const globalCurrency: ICurrency | undefined = useAppSelector(
    (state) => state.app.currentCurrency
  );
  const [hydrated, setHydrated] = useState(false);

  const calculateServiceCurrencyAndPrice = useCallback(
    (
      applicationClassPrices: IApplicationClassPrice[],
      searchClassPrices: IApplicationClassPrice[],
      finalFees: IApplicationClassPrice[]
    ) => {
      return calculateServicePricesWithCurrency(
        { applicationClassPrices, searchClassPrices, finalFees },
        globalCurrency?.conversionRate || 1,
        appState.currentCurrency?.isoCode || 'USD',
        appState.currentLocale || 'en'
      );
    },
    [
      appState.currentCurrency?.isoCode,
      appState.currentLocale,
      globalCurrency?.conversionRate,
    ]
  );

  const heroCountryProps: IHeroCountry = {
    title: props.pageTitle,
    titleHighlightedText: props.pageTitleHighlightedText,
    countryCode: props.countryIso,
    tips: [
      {
        text: `**${props.customerReviewsMeta.average}** (+${props.customerReviewsMeta.numberOfReviews})`,
        iconPath: '/assets/images/icons/StarRounded.svg',
        action: () => {
          scrollToElement('customer-reviews');
        },
      },
      {
        text: props.heroTipOne,
        iconPath: '/assets/images/icons/ClockIcon.svg',
      },
      {
        text: props.heroTipTwo,
        iconPath: '/assets/images/icons/PersonIcon.svg',
      },
      {
        text: props.heroTipThree,
        iconPath: '/assets/images/icons/CoinsIcon.svg',
      },
    ],
  };

  const requestCallToActionProps: IRequestCallToActionProps = {
    text: props.pageSubTitle,
    fromPrice: calculateServiceCurrencyAndPrice(
      props.countryApplicationClassPrices,
      props.countrySearchClassPrices,
      props.finalFees
    ).currencyApplicationClassPrice,
    fromLabel: props.fromLabel,
    feesIncludedLabel: props.feesIncludedLabel,
    button: {
      text: props.orderNowLabel,
      link:
        props.countryIso3 !== 'WPX'
          ? `${props.trademarkLink}?countries=${props.countryIso}`
          : undefined,
      action:
        props.countryIso3 !== 'WPX'
          ? undefined
          : () => {
              scrollToContactUs();
            },
    },
  };

  const topCallToActionProps: IBoxedCallToActionProps = {
    text: props.topCallToAction?.content || '',
    button: {
      text:
        props.topCallToAction?.buttons &&
        props.topCallToAction.buttons.length > 0
          ? props.topCallToAction.buttons[0].label
          : '',
      link:
        props.topCallToAction?.buttons &&
        props.topCallToAction.buttons.length > 0
          ? props.topCallToAction.buttons[0].path
          : '',
    },
    isLargeTitle: true,
  };

  const bottomCallToActionProps: IBoxedCallToActionProps = {
    title: props.bottomCallToAction?.title || '',
    text: props.bottomCallToAction?.content || '',
    button: {
      text:
        props.bottomCallToAction?.buttons &&
        props.bottomCallToAction.buttons.length > 0
          ? props.bottomCallToAction.buttons[0].label
          : '',
      link:
        props.bottomCallToAction?.buttons &&
        props.bottomCallToAction.buttons.length > 0
          ? props.bottomCallToAction.buttons[0].path
          : '',
    },
    isDark: true,
  };

  const businessKeyValueProps: ISellingPoints = {
    title: props.businessKeyValues.title,
    description: props.businessKeyValues.description,
    sellingPointsItems: [
      {
        title: props.businessKeyValues.keyValueOneTitle,
        description: props.businessKeyValues.keyValueOneDescription,
        iconPath: '/assets/images/icons/ExpertAgentIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueTwoTitle,
        description: props.businessKeyValues.keyValueTwoDescription,
        iconPath: '/assets/images/icons/WorldWideServicesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueThreeTitle,
        description: props.businessKeyValues.keyValueThreeDescription,
        iconPath: '/assets/images/icons/NoHiddenChargesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueFourTitle,
        description: props.businessKeyValues.keyValueFourDescription,
        iconPath: '/assets/images/icons/FreeConsultIcon.svg',
      },
    ],
  };

  const trustedByProps: ITrustedByProps = {
    title: props.trustedBy.title,
    companies: props.trustedBy.logos.map((x) => ({
      company: 'company',
      image: (
        <Image
          quality={90}
          src={x.url as string}
          fill
          sizes="175px"
          alt={x.alternativeText || `Partner logo ${x.name}`}
          loading="lazy"
        />
      ),
    })),
  };

  const reviewsCarouselProps: IReviewsCarouselProps = {
    averageScore: props.customerReviewsMeta.average,
    totalReviews: props.customerReviewsMeta.numberOfReviews,
    topTipStart: props.customerReviewsTopTipStart,
    topTipEnd: props.customerReviewsTopTipEnd,
    title: props.customerReviews.title,
    description: props.customerReviews.description,
    bottomTip: props.customerReviewsBottomTip,
    defaultLinkPath: props.customerReviews.buttonPath,
    reviews: props.reviews.map((review) => ({
      text: review.content,
      username: review.customerName,
      score: 5,
      date: formatDate(review.date),
      link: review.link,
    })),
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.formSuccessMessage,
      submitErrorMessage: props.formErrorMessage,
    },
  };

  const callToActionWithImageComponentProps = {
    title: props.howToCallToAction?.title || '',
    content: props.howToCallToAction?.content || '',
    image: props.howToCallToAction?.image!,
    buttons:
      props.howToCallToAction?.buttons?.map((button, index) => {
        if (index === 0) {
          button.action = () => scrollToElement('contact');
        } else if (index === 1) {
          button.action = () => scrollToElement('services');
        }
        return button;
      }) || [],
  };

  const doItYourselfIconsWithBulletListsProps: IIconsWithBulletListsProps = {
    items: [
      {
        title: props.doItYourselfList.title,
        iconPath: '/assets/images/icons/DIYIcon.svg',
        list: props.doItYourselfList.items,
      },
      {
        title: props.professionalList.title,
        iconPath: '/assets/images/icons/ProfessionalIcon.svg',
        list: props.professionalList.items,
      },
    ],
  };

  const faqsItems: IAccordionListItem[] =
    props.faqs?.map((faq) => {
      const prices = calculateServiceCurrencyAndPrice(
        props.countryApplicationClassPrices,
        props.countrySearchClassPrices,
        props.finalFees
      );
      const placeholderValues = {
        estimatedProcessingMonths: props.estimatedProcessingMonths,
        yearsOfValidity: props.yearsOfValidity,
        requiredDocuments: props.requiredDocuments,
        publicPriceNotes: props.publicPriceNotes,
      };
      const faqContent = replaceContentPlaceholders(faq.content, {
        ...prices,
        ...placeholderValues,
      });

      return {
        ...faq,
        content: faqContent,
      };
    }) || [];

  useEffect(() => {
    setHydrated(true);
  }, []);

  const CallToActionWithImageComponent = dynamic(
    () => import('../../components/common/blocks/CallToActionWithImage')
  );

  const ContactComponent = dynamic(
    () => import('../../components/contact-us/Contact'),
    {
      ssr: false,
    }
  );

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <section className="page-content-section">
        <header>
          <div className="page-content-block">
            <HeroCountry {...heroCountryProps} />
          </div>
          <div className="page-content-block">
            <RequestCallToAction {...requestCallToActionProps} />
          </div>
          {props.countryKeyFactsProps &&
            props.countryKeyFactsProps.facts?.length > 0 && (
              <div className="page-content-block">
                <KeyFacts {...props.countryKeyFactsProps} />
              </div>
            )}
        </header>
        <div className="page-content-block">
          <BoxedCallToAction {...topCallToActionProps} />
        </div>
      </section>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <CallToActionWithImageComponent
            {...callToActionWithImageComponentProps}
          />
        </div>
      </div>

      {faqsItems.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <AccordionList title={props.faqsTitle || ''} items={faqsItems} />
          </div>
        </div>
      )}

      <div className="page-content-section">
        <div className="page-content-block" id="customer-reviews">
          {hydrated && <ReviewsCarousel {...reviewsCarouselProps} />}
        </div>
      </div>

      {/* <div className="page-content-section background-light">
        <div className="page-content-block">
          <TrustedByComponent {...trustedByProps} />
        </div>
      </div> */}

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact-us">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <div className="section-heading">
            <ReactMarkdown>
              {props.doItYourselfVsProfessionalTitle}
            </ReactMarkdown>
          </div>
          <IconsWithBulletLists {...doItYourselfIconsWithBulletListsProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <SellingPoints {...businessKeyValueProps} />
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <BoxedCallToAction {...bottomCallToActionProps} />
        </div>
      </div>

      {props.relatedServices.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <RelatedServices
              title={props.relatedServicesTitle}
              services={props.relatedServices}
            />
          </div>
        </div>
      )}

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToAction {...props.featuredServicesCallToActionProps} />
        </div>
      </div>
    </div>
  );
};

TrademarkRegistrationCountryLandingPage.getLayout = function getLayout(
  page: ReactElement
) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      noindex={page.props.meta.noindex}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

function fallbackCountryMeta(
  locale: string,
  meta: IMetaComponent[],
  countryCode: string
) {
  const localizedMeta = getTranslation(meta, locale);
  const fallback = {
    title: localizedMeta.title || generateRandomId(),
    description: localizedMeta.description || generateRandomId(),
    slug: !!localizedMeta.slug
      ? `${localizedMeta.slug}/${countryCode}`
      : generateRandomId(),
    language: locale,
  };
  return fallback;
}

export async function getStaticPaths(context: any) {
  let paths: any[] = [];
  const trademarkLandingsUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const trademarkRegistrationCountryLandings: ITrademarkRegistrationCountryLanding[] =
    flatStrapiCollection(
      await fetchStrapi<any>(
        `/trademark-registration-landings`,
        trademarkLandingsUrlParams,
        buildApiKey
      )
    );

  for (const locale of context.locales) {
    const localizedPaths = trademarkRegistrationCountryLandings.map((item) => {
      const localizedMeta = getTranslation(item.meta, locale);
      return {
        params: {
          slug: localizedMeta.slug,
        },
        locale: locale,
      };
    });
    paths = [...paths, ...localizedPaths];
  }

  return { paths, fallback: false };
}

export async function getStaticProps(
  context: any
): Promise<{ props: ITrademarkRegistrationCountryLandingProps }> {
  const appRoutes = await getRoutes();

  const getLocalizedAppRoute = (actualPath: string) => {
    return (
      appRoutes?.find(
        (x: IAppPage) =>
          x &&
          x.destination.replace(`/${context.locale}`, '') === actualPath &&
          x.language === context.locale
      )?.source || actualPath
    );
  };

  const paramSlug = context.params.slug;

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'heroTipsTitle',
    'populate[2]': 'heroTipOne',
    'populate[3]': 'heroTipTwo',
    'populate[4]': 'heroTipThree',
    'populate[5]': 'businessKeyValues',
    'populate[6]': 'trustedBy',
    'populate[7]': 'trustedBy.logos',
    'populate[8]': 'customerReviews',
    'populate[9]': 'fromLabel',
    'populate[10]': 'orderNowLabel',
    'populate[11]': 'contactUsForm',
    'populate[12]': 'formSuccessMessage',
    'populate[13]': 'formErrorMessage',
    'populate[14]': 'relatedServicesTitle',
    'populate[15]': 'topCallToActionRegistration',
    'populate[16]': 'bottomCallToAction',
    'populate[17]': 'topCallToActionRegistration.buttons',
    'populate[18]': 'bottomCallToAction.buttons',
    'populate[19]': 'requiredDocuments',
    'populate[20]': 'publicPriceNotes',
    'populate[21]': 'countryKeyFactsRegistration',
    'populate[22]': 'countryKeyFactsRegistration.facts',
    'populate[23]': 'countryKeyFactsRegistration.facts.icon',
  });

  const trademarkLandingUrlParams = new URLSearchParams({
    'filters[meta][slug][$eq]': paramSlug,
    'populate[0]': 'country',
    'populate[1]': 'country.searchClassPrices',
    'populate[2]': 'country.applicationClassPrices',
    'populate[3]': 'country.finalFees',
    'populate[4]': 'country.currency',
    'populate[5]': 'country.nameTranslations',
    'populate[6]': 'meta',
    'populate[7]': 'breadcrumbAncestors',
    'populate[8]': 'pageTitle',
    'populate[9]': 'pageTitleHighlightedText',
    'populate[10]': 'pageSubTitle',
    'populate[11]': 'orderNowCallToActionTitle',
    'populate[12]': 'orderNowCallToActionText',
    'populate[13]': 'howToCallToAction',
    'populate[14]': 'howToCallToAction.buttons',
    'populate[15]': 'howToCallToAction.image',
    'populate[16]': 'doItYourselfVsProfessionalTitle',
    'populate[17]': 'doItYourselfList',
    'populate[18]': 'professionalList',
    'populate[19]': 'customerReviews',
    'populate[20]': 'faqs',
    'populate[21]': 'faqsTitle',
    'populate[22]': 'topCallToAction',
    'populate[23]': 'bottomCallToAction',
    'populate[24]': 'topCallToAction.buttons',
    'populate[25]': 'bottomCallToAction.buttons',
    'populate[26]': 'country.requiredDocuments',
    'populate[27]': 'country.publicPriceNotes',
    'populate[28]': 'countryKeyFacts',
    'populate[29]': 'countryKeyFacts.facts',
    'populate[30]': 'countryKeyFacts.facts.icon',
  });

  const customerReviewsUrlParams = {
    locale: context.locale,
  };

  // Base page data
  const countryBasePage = (
    await fetchStrapi<any>(`/country-page`, pageUrlParams, buildApiKey)
  ).data.attributes as ICountryPage;
  const trustedBy: ITrustedByComponent = getTranslation(
    countryBasePage.trustedBy,
    context.locale
  );

  // Trademark registration country landing data
  const trademarkRegistrationCountryLandingResult = await fetchStrapi<any>(
    `/trademark-registration-landings`,
    trademarkLandingUrlParams,
    buildApiKey
  );
  const trademarkRegistrationCountryLanding =
    flatStrapiCollection<ITrademarkRegistrationCountryLanding>(
      trademarkRegistrationCountryLandingResult
    )[0];

  // Customer reviews with fallback to country base page
  const customerReviews: ICustomerReviewsComponent =
    trademarkRegistrationCountryLanding.customerReviews &&
    trademarkRegistrationCountryLanding.customerReviews.length > 0
      ? getTranslation(
          trademarkRegistrationCountryLanding.customerReviews,
          context.locale
        )
      : getTranslation(countryBasePage.customerReviews, context.locale);

  // Call to actions with fallback to country base page
  const topCallToAction: ICallToActionComponent =
    trademarkRegistrationCountryLanding.topCallToAction &&
    trademarkRegistrationCountryLanding.topCallToAction.length > 0
      ? getTranslation(
          trademarkRegistrationCountryLanding.topCallToAction,
          context.locale
        )
      : getTranslation(
          countryBasePage.topCallToActionRegistration,
          context.locale
        );
  const bottomCallToAction: ICallToActionComponent =
    trademarkRegistrationCountryLanding.bottomCallToAction &&
    trademarkRegistrationCountryLanding.bottomCallToAction.length > 0
      ? getTranslation(
          trademarkRegistrationCountryLanding.bottomCallToAction,
          context.locale
        )
      : getTranslation(countryBasePage.bottomCallToAction, context.locale);

  const reviews = (
    flatStrapiCollection(
      await fetchStrapi<any>(
        `/customer-reviews`,
        customerReviewsUrlParams,
        buildApiKey
      )
    ) as ICustomerReview[]
  ).map((x) => removeObjectProperties(x, ['id', 'createdAt', 'updatedAt']));
  const contactMethods = (await getContactMethods()).map((x) =>
    removeObjectProperties(x, ['id', 'createdAt', 'updatedAt'])
  );
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = removeObjectProperties(
    await populateFooterProps(context.locale),
    ['id', 'createdAt', 'updatedAt']
  );
  const pageMeta =
    getTranslation(trademarkRegistrationCountryLanding.meta, context.locale) ||
    fallbackCountryMeta(
      context.locale,
      countryBasePage.meta,
      trademarkRegistrationCountryLanding.country?.iso || ''
    );
  const originalPageMeta = getTranslation(
    trademarkRegistrationCountryLanding.meta,
    'en'
  );
  const pageTitle = getTranslation(
    trademarkRegistrationCountryLanding.pageTitle,
    context.locale
  ).text as string;
  const pageTitleHighlightedText =
    trademarkRegistrationCountryLanding.pageTitleHighlightedText &&
    trademarkRegistrationCountryLanding.pageTitleHighlightedText.length > 0
      ? (getTranslation(
          trademarkRegistrationCountryLanding.pageTitleHighlightedText,
          context.locale
        ).text as string)
      : null;
  const pageSubTitle = getTranslation(
    trademarkRegistrationCountryLanding.pageSubTitle,
    context.locale
  ).text as string;
  const doItYourselfVsProfessionalTitle = getTranslation(
    trademarkRegistrationCountryLanding.doItYourselfVsProfessionalTitle,
    context.locale
  ).text as string;
  const alternativeLinks = getAlternateLinks(
    `/trademark-registration-services/${originalPageMeta.slug}`,
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', pageMeta.slug);
  const trademarkLink = getLocalizedAppRoute('/trademark-registration');
  const commonTranslations = await getCommonTranslations();
  const howToCallToAction = getTranslation(
    trademarkRegistrationCountryLanding.howToCallToAction,
    context.locale
  );
  if (howToCallToAction.image && (howToCallToAction.image as any).data) {
    howToCallToAction.image = flatStrapiObject(howToCallToAction.image);
  }

  // Data mapping and clean up
  trademarkRegistrationCountryLanding.country = flatStrapiObject(
    trademarkRegistrationCountryLanding.country as any
  );
  trustedBy.logos = flatStrapiCollection(trustedBy.logos as any).map((x) =>
    keepObjectProperties<IMedia>(x, ['url', 'alternativeText', 'name'])
  );
  navBarProps.items = removeObjectProperties(navBarProps.items, [
    'id',
    'createdAt',
    'updatedAt',
  ]);

  // Faqs
  const faqsTitle =
    trademarkRegistrationCountryLanding.faqsTitle &&
    trademarkRegistrationCountryLanding.faqsTitle.length > 0
      ? (getTranslation(
          trademarkRegistrationCountryLanding.faqsTitle,
          context.locale
        ).text as string)
      : (getTranslation(commonTranslations.faqsTitle, context.locale)
          .text as string);
  const faqs = trademarkRegistrationCountryLanding?.faqs
    ?.filter((x) => x.language === context.locale)
    .map((x) => ({
      title: x.question,
      content: x.answer,
    })) as IAccordionListItem[];

  // Related services
  const countryRelatedServicesUrlParams = {
    iso: trademarkRegistrationCountryLanding.country.iso,
    locale: context.locale,
    apiNamesToExclude: [
      'trademark-registration-landing',
      'copyright-landing',
      'industrial-design-landing',
    ],
  };
  const countryRelatedServices = await fetchStrapi<ICountryRelatedService[]>(
    `/countries/related-services`,
    countryRelatedServicesUrlParams,
    buildApiKey
  );

  // Generate JSON-LD schema
  const currencies = await getAppCurrencies();
  const registrationServiceName = commonTranslations.serviceTypeApplication;
  const serviceSchema = generateTrademarkCountryLandingServiceSchema(
    trademarkRegistrationCountryLanding,
    getTranslation(registrationServiceName, context.locale).text as string,
    currencies,
    canonicalLink,
    context.locale
  );

  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;

  // FAQs schema generation --- START
  // The following elements are used to replace FAQs placeholders with actual values
  const countryApplicationClassPrices = trademarkRegistrationCountryLanding
    .country.applicationClassPrices as IApplicationClassPrice[];

  const countrySearchClassPrices = trademarkRegistrationCountryLanding.country
    .searchClassPrices as IApplicationClassPrice[];

  const finalFees = trademarkRegistrationCountryLanding.country
    .finalFees as IApplicationClassPrice[];

  // Because the currency is dynamic and can be changed by the user,
  // we need to calculate the prices with the default currency (USD) and conversion rate
  const prices = calculateServicePricesWithCurrency(
    {
      applicationClassPrices: countryApplicationClassPrices,
      searchClassPrices: countrySearchClassPrices,
      finalFees,
    },
    1,
    'USD',
    context.locale
  );

  const estimatedProcessingMonths = trademarkRegistrationCountryLanding.country
    .estimatedProcessingMonths
    ? trademarkRegistrationCountryLanding.country.estimatedProcessingMonths.toString()
    : '';

  const yearsOfValidity = trademarkRegistrationCountryLanding.country
    .yearsOfValidity
    ? trademarkRegistrationCountryLanding.country.yearsOfValidity.toString()
    : '';

  const requiredDocuments = getTranslation(
    trademarkRegistrationCountryLanding.country.requiredDocuments,
    context.locale
  ).text as string;

  const publicPriceNotes = getTranslation(
    trademarkRegistrationCountryLanding.country.publicPriceNotes,
    context.locale
  ).text as string;

  const placeholderValues = {
    estimatedProcessingMonths,
    yearsOfValidity,
    requiredDocuments,
    publicPriceNotes,
    average: customerReviewsMeta.average,
    numberOfReviews: customerReviewsMeta.numberOfReviews,
    bestRating: customerReviewsMeta.bestRating,
  };

  const faqsSchema = generateFaqsSchema(
    faqs.map((x) => ({
      question: x.title,
      answer: replaceContentPlaceholders(x.content, {
        ...prices,
        ...placeholderValues,
      }),
    }))
  );
  // FAQs schema generation --- END

  const productSchema = await generateProductSchema(
    getTranslation(registrationServiceName, context.locale).text as string,
    customerReviewsMeta
  );

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );

  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );

  const customerReviewsSchema = generateCustomerReviewsSchema(
    reviews,
    customerReviewsMeta
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [
      ...websiteSchema,
      ...organizationSchema,
      ...serviceSchema,
      ...faqsSchema,
      ...productSchema,
      ...customerReviewsSchema,
    ],
  };

  // Populate breadcrumbs
  if (
    trademarkRegistrationCountryLanding.breadcrumbAncestors &&
    trademarkRegistrationCountryLanding.breadcrumbAncestors.length > 0
  ) {
    navBarProps.breadcrumbAncestors =
      trademarkRegistrationCountryLanding.breadcrumbAncestors?.filter(
        (x) => x.language === context.locale
      );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = pageTitle;

  // Check if customer reviews title contains placecholders. Example: "Average rating: {{average}} based on {{numberOfReviews}} reviews (Best rating: {{bestRating}})"
  if (
    customerReviews.title.includes('{{') &&
    customerReviews.title.includes('}}')
  ) {
    customerReviews.title = customerReviews.title.replace(
      '{{average}}',
      customerReviewsMeta.average.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{numberOfReviews}}',
      customerReviewsMeta.numberOfReviews.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{bestRating}}',
      customerReviewsMeta.bestRating.toString()
    );
  }

  // Prepare Featured Services Call To Action component props
  const featuredServicesCallToAction: ICallToActionComponent = getTranslation(
    commonTranslations.featuredServicesCallToAction,
    context.locale
  );

  const featuredServicesCallToActionProps: ICallToActionProps = {
    title: featuredServicesCallToAction.title,
    subtitle: featuredServicesCallToAction.content,
    buttons: featuredServicesCallToAction.buttons?.map((button) => ({
      label: button.label,
      path: button.path,
    })),
  };

  const formSuccessMessage = isUndefinedOrEmpty(
    countryBasePage.formSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(countryBasePage.formSuccessMessage, context.locale)
        .text as string);
  const formErrorMessage = isUndefinedOrEmpty(countryBasePage.formErrorMessage)
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(countryBasePage.formErrorMessage, context.locale)
        .text as string);

  // Key facts generation with fallback to country base page if not available in the landing page
  const countryKeyFactsWithFallback = isUndefinedOrEmpty(
    trademarkRegistrationCountryLanding.countryKeyFacts
  )
    ? getTranslation(
        countryBasePage.countryKeyFactsRegistration,
        context.locale
      )
    : getTranslation(
        trademarkRegistrationCountryLanding.countryKeyFacts,
        context.locale
      );

  // Regenerate the countryKeyFactsProps to be used in the component, ensuring that the iconUrl is correctly set
  const countryKeyFactsProps: IKeyFactsProps = {
    title: countryKeyFactsWithFallback.title || '',
    facts: (countryKeyFactsWithFallback.facts || []).map((item) => {
      const values = {
        ...prices,
        ...placeholderValues,
      };
      const title = replaceContentPlaceholders(item.title, values);
      const text = replaceContentPlaceholders(item.text, values);
      const tip = replaceContentPlaceholders(item.tip || '', values);
      const icon = flatStrapiObject<IMedia>(item.icon); // Map the icon property to the actual icon object

      return {
        title,
        text,
        tip,
        anchor: item.anchor || '',
        iconUrl: icon.url || '',
      };
    }),
  };

  const props: ITrademarkRegistrationCountryLandingProps = {
    // Layout
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta: pageMeta,
    pageTitle,
    pageTitleHighlightedText,
    pageSubTitle,
    heroTipsTitle: getTranslation(countryBasePage.heroTipsTitle, context.locale)
      .text as string,
    heroTipOne: getTranslation(countryBasePage.heroTipOne, context.locale)
      .text as string,
    heroTipTwo: getTranslation(countryBasePage.heroTipTwo, context.locale)
      .text as string,
    heroTipThree: getTranslation(countryBasePage.heroTipThree, context.locale)
      .text as string,
    howToCallToAction,
    doItYourselfVsProfessionalTitle,
    doItYourselfList: getTranslation(
      trademarkRegistrationCountryLanding.doItYourselfList,
      context.locale
    ),
    professionalList: getTranslation(
      trademarkRegistrationCountryLanding.professionalList,
      context.locale
    ),
    businessKeyValues: getTranslation(
      countryBasePage.businessKeyValues,
      context.locale
    ),
    trustedBy,
    customerReviews,
    reviews,
    fromLabel: getTranslation(countryBasePage.fromLabel, context.locale)
      .text as string,
    feesIncludedLabel: getTranslation(
      commonTranslations.feesIncluded,
      context.locale
    ).text as string,
    orderNowLabel: getTranslation(countryBasePage.orderNowLabel, context.locale)
      .text as string,
    contactMethods,
    formSuccessMessage,
    formErrorMessage,
    contactUsForm: getTranslation(
      countryBasePage.contactUsForm,
      context.locale
    ),
    countryIso: trademarkRegistrationCountryLanding.country.iso,
    countryIso3: trademarkRegistrationCountryLanding.country.iso3,
    countryApplicationClassPrices,
    countrySearchClassPrices,
    finalFees,
    alternativeLinks,
    canonicalLink,
    trademarkLink,
    relatedServicesTitle: getTranslation(
      countryBasePage.relatedServicesTitle,
      context.locale
    ).text as string,
    relatedServices: countryRelatedServices.map((x) => ({
      title: x.title,
      link: getLocalizedAppRoute(x.slug),
    })),
    faqsTitle,
    faqs,
    pageSchema,
    featuredServicesCallToActionProps: featuredServicesCallToActionProps,
    topCallToAction,
    bottomCallToAction,
    customerReviewsMeta,
    customerReviewsTopTipStart: getTranslation(
      commonTranslations.customerReviewsTopTipStart,
      context.locale
    ).text as string,
    customerReviewsTopTipEnd: getTranslation(
      commonTranslations.customerReviewsTopTipEnd,
      context.locale
    ).text as string,
    customerReviewsBottomTip: getTranslation(
      commonTranslations.customerReviewsBottomTip,
      context.locale
    ).text as string,
    estimatedProcessingMonths,
    yearsOfValidity,
    requiredDocuments,
    publicPriceNotes,
    countryKeyFactsProps,
  };

  return {
    props,
  };
}

export default TrademarkRegistrationCountryLandingPage;
